<template>
  <v-container fluid>
    <div id="content">

      <div class="card-grid" v-if="!loading">
        
        <div class="widget-ctrl mb-4">
          <v-row no-gutters>
            <v-col cols="0" sm="4" md="6"></v-col>
            <v-col cols="6" sm="4" md="3" class="pl-2">
              <v-combobox
                v-if="corporateLabels.length > 1"
                ref="corporate"
                label="企業を選択"
                v-model="filterKeyword.corporate"
                :items="corporateLabels"
                append-icon="fas fa-list-ol"
                @click.stop="openSelected('corporate')"
                @change="redraw"
                readonly outlined dense hide-details="auto">
              </v-combobox>
            </v-col>
            <v-col cols="6" sm="4" md="3" class="pl-2">
              <v-btn outlined block @click.stop="widgetSetting = true">
                <v-icon left>far fa-window-maximize</v-icon>
                ウィジェット設定
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <div
          v-masonry
          transition-duration="0.3s"
          item-selector=".grid-item">

          <div v-masonry-tile class="grid-item" v-if="isShow('カレンダー')">
            <div class="item">
              <div class="title">
                カレンダー
                <template v-if="monthStart">
                  ({{ nowMonth }})
                </template>
                <div class="ctrl-btns">
                  <v-btn icon @click="monthPrev">
                    <v-icon>fas fa-chevron-circle-left</v-icon>
                  </v-btn>
                  <v-btn icon @click="monthNext">
                    <v-icon>fas fa-chevron-circle-right</v-icon>
                  </v-btn>
                </div>
              </div>
              <v-sheet min-height="420">
                <v-calendar
                  ref="calendar"
                  v-model="monthStart"
                  :show-month-on-first="false"
                  :day-format="timestamp => new Date(timestamp.date).getDate()"
                  :events="events"
                  @change="viewChange">
                </v-calendar>
              </v-sheet>
            </div>
          </div>

          <div v-masonry-tile class="grid-item" v-if="isShow('カレンダー')">
            <div class="item">
              <div class="title">
                有給
              </div>
              <v-sheet>
                <v-row no-gutters>
                  <v-col cols="3" class="pa-2">
                    <v-card outlined>
                      <v-card-text>残日数</v-card-text>
                      <v-card-text class="text-right">
                        <template v-if="paidRemain != null">{{ paidRemain }} 日</template>
                        <template v-else>--</template>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="3" class="pa-2">
                    <v-card outlined>
                      <v-card-text>使用済時間有給</v-card-text>
                      <v-card-text class="text-right">
                        <template v-if="paidHoursUsed != null">{{ paidHoursUsed }} h /</template>
                        <template v-else>-- /</template>
                        {{ appOptions.maxDaysOfPaidTime * dailyWorktime }} h
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-sheet>
            </div>
          </div>

          <div v-masonry-tile class="grid-item" v-if="isShow('入金予定')">
            <div class="item">
              <div class="title">
                入金予定
                <div class="ctrl-btns">
                  <v-btn 
                    color="primary"
                    @click="inputRegistDialog = true" 
                    tile text small >
                    <v-icon left>fas fa-save</v-icon> 未請求の入金登録
                  </v-btn>
                  <v-btn 
                    color="primary"
                    @click="inputDialog = true" 
                    :disabled="!selectedInputs.length > 0"
                    tile text small >
                    <v-icon left>fas fa-check</v-icon> 日付を指定して計上
                  </v-btn>
                </div>
              </div>
              <v-data-table
                :headers="inputHeaders"
                :items="filterInputPayments"
                :sort-by="['claimDate']"
                :sort-desc="[true]"
                :loading="loading"
                :items-per-page="5"
                v-model="selectedInputs"
                show-select dense>
                <template v-slot:item.customer="{ item }">
                  {{ item.customer.name }}
                </template>
                <template v-slot:item.amount="{ item }">
                  &yen; {{ separate( Math.floor(item.amount * (item.tax / 100 + 1)) ) }}
                </template>
                <template v-slot:header.data-table-select="{ props, on }">
                  <v-simple-checkbox v-bind="props" v-on="on" color="blue darken-1" v-ripple></v-simple-checkbox>
                </template>
                <template v-slot:item.data-table-select="{ isSelected, select }">
                  <v-simple-checkbox :value="isSelected" @input="select($event)" color="blue lighten-2" v-ripple></v-simple-checkbox>
                </template>
                <template v-slot:no-data>
                  データがありません
                </template>
              </v-data-table>
            </div>
          </div>

          <div v-masonry-tile class="grid-item" v-if="isShow('支払予定')">
            <div class="item">
              <div class="title">
                支払予定
                <div class="ctrl-btns">
                  <v-btn 
                    color="primary"
                    @click="outputDialog = true" 
                    :disabled="!selectedOutputs.length > 0"
                    tile text small>
                    <v-icon left>fas fa-check</v-icon> 日付を指定して計上
                  </v-btn>
                </div>
              </div>
              <v-data-table
                :headers="outputHeaders"
                :items="filterOutputPayments"
                :sort-by="['fixedDate']"
                :sort-desc="[true]"
                :loading="loading"
                :items-per-page="5"
                v-model="selectedOutputs"
                @click:row="onClaimClick"
                show-select dense>
                <template v-slot:item.customer="{ item }">
                  {{ item.customer.name }}
                </template>
                <template v-slot:item.amount="{ item }">
                  &yen; {{ separate( Math.floor(item.amount) ) }}
                </template>
                <template v-slot:header.data-table-select="{ props, on }">
                  <v-simple-checkbox v-bind="props" v-on="on" color="orange darken-1" v-ripple></v-simple-checkbox>
                </template>
                <template v-slot:item.data-table-select="{ isSelected, select }">
                  <v-simple-checkbox :value="isSelected" @input="select($event)" color="orange lighten-2" v-ripple></v-simple-checkbox>
                </template>
                <template v-slot:no-data>
                  データがありません
                </template>
              </v-data-table>
            </div>
          </div>

          <div v-masonry-tile class="grid-item" v-if="isShow('請求書類')">
            <div class="item">
              <div class="title">
                請求書類まとめてアップロード
              </div>
              <v-sheet>
                <MultiUpload
                  :files="paymentDocs"
                  @loadedFile="redraw"
                  @uploadFile="paymentDocMultiUpload"
                  @lightbox="photoPreview"
                  accept="image/*">
                </MultiUpload>
              </v-sheet>
            </div>
          </div>

          <div v-masonry-tile class="grid-item" v-if="isShow('売上グラフ')">
            <div class="item weeklySales">
              <div class="title">
                売上グラフ
                <template v-if="yearStart">
                  ({{ nowYear }})
                </template>
                <div class="ctrl-btns">
                  <v-btn icon @click="yearPrev">
                    <v-icon>fas fa-chevron-circle-left</v-icon>
                  </v-btn>
                  <v-btn icon @click="yearNext">
                    <v-icon>fas fa-chevron-circle-right</v-icon>
                  </v-btn>
                </div>
              </div>
              <weeklySales
                :chart-data="monthlySalesData"
                :height="weeklySalesOptions.height" />
            </div>
          </div>

          <div v-masonry-tile class="grid-item" v-if="isShow('売上履歴')">
            <div class="item salesHistory">
              <div class="title">
                売上履歴
                <template v-if="monthStart">
                  ({{ nowMonth }})
                </template>
                <div class="ctrl-btns">
                  <v-btn icon @click="monthPrev">
                    <v-icon>fas fa-chevron-circle-left</v-icon>
                  </v-btn>
                  <v-btn icon @click="monthNext">
                    <v-icon>fas fa-chevron-circle-right</v-icon>
                  </v-btn>
                </div>
              </div>
              <v-data-table
                :headers="historyHeaders"
                :items="orderHistory"
                :sort-by="['claimDate']"
                :sort-desc="[true]"
                :loading="loading"
                dense>
                <template v-slot:item.customer="{ item }">
                  {{ item.customer.name }}
                </template>
                <template v-slot:item.complete="{ item }">
                  <template v-if="item.complete">
                    <v-icon color="green darken-2">far fa-check-circle</v-icon>
                  </template>
                </template>
                <template v-slot:item.amount="{ item }">
                  &yen; {{ separate( Math.floor(item.amount * (item.tax / 100 + 1)) ) }}
                </template>
              </v-data-table>
              <div class="total">
                <span class="label">予定</span>
                <span class="value">&yen; {{ separate(historyTotalValue) }}</span>
                <span class="label">実績</span>
                <span class="value">&yen; {{ separate(historyTotalResult) }}</span>
              </div>
            </div>
          </div>

          <div v-masonry-tile class="grid-item" v-if="isShow('月間予約時間')">
            <div class="item">
              <div class="title">
                月間予約時間
                <template v-if="monthStart">
                  ({{ nowMonth }})
                </template>
                <div class="ctrl-btns">
                  <v-btn icon @click="monthPrev">
                    <v-icon>fas fa-chevron-circle-left</v-icon>
                  </v-btn>
                  <v-btn icon @click="monthNext">
                    <v-icon>fas fa-chevron-circle-right</v-icon>
                  </v-btn>
                </div>
              </div>
              <MonthlyReserve
                :chart-data="monthlyReserveData"
                :height="MonthlyReserveOptions.height" />
            </div>
          </div>

          <div v-masonry-tile class="grid-item full" v-if="isShow('工程表')">
            <div class="item">
              <div class="title">工程表</div>
              <!-- 工程表 -->
              <ScheduleTimeline
                v-if="resources.length > 0"
                :tasks="resources">
              </ScheduleTimeline>
            </div>
          </div>
        </div>
      </div>

      <!-- ウィジェットの表示変更 -->
      <v-bottom-sheet
        v-model="widgetSetting"
        persistent>
        <v-sheet class="text-center pa-5">
          <ul class="list-inline widgets">
            <template v-for="widget in userWidgets">
              <li :key="widget.name" 
                @click.stop="showChange(widget.name)"
                :class="isShow(widget.name) ? 'active' : ''">
                {{ widget.name }}
              </li>
            </template>
          </ul>
          <v-btn @click.stop="widgetSetting = false" text>
            <v-icon left>fas fa-times</v-icon>閉じる
          </v-btn>
        </v-sheet>
      </v-bottom-sheet>

      <!-- 入金の一括確認ダイアログ -->
      <v-navigation-drawer v-model="inputDialog" fixed temporary right stateless width="600" id="input-dialog">
        <v-card flat>
          <v-card-title>
            <div class="title">入金予定を日付指定して計上</div>
          </v-card-title>
          <v-card-text>
            <v-form v-model="inputValid">
              <v-row no-gutters>
                <v-col cols="12" class="pa-2">
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">請求日</th>
                          <th class="text-left">客先</th>
                          <th class="text-right">合計金額（税込）</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in selectedInputs" :key="item.id">
                          <td>{{ item.claimDate }}</td>
                          <td>{{ item.customer.name }}</td>
                          <td class="text-right">&yen; {{ separate( Math.floor(item.amount * (item.tax / 100 + 1)) ) }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-col>
                <v-col cols="6" class="pa-2 mt-5">
                  <v-menu
                    v-model="inputPayment"
                    :close-on-content-click="false"
                    :nudge-right="0"
                    transition="scale-transition" offset-y min-width="290px">
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        ref="inputPaymentDate"
                        label="入金日"
                        v-model="inputPaymentDate"
                        :rules="[rules.required]"
                        class="required"
                        v-on="on"
                        outlined clearable hide-details="auto">
                      </v-text-field>
                    </template>
                    <v-date-picker v-model="inputPaymentDate" @input="inputPayment = false"></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" @click.stop="checkInputs" :loading="saving" :disabled="!inputValid" text>
              <v-icon left>fas fa-save</v-icon>登録
            </v-btn>
            <v-btn @click="inputDialog = false" text>
              <v-icon left>fas fa-times</v-icon>閉じる
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-navigation-drawer>

      <!-- 支払の一括確認ダイアログ -->
      <v-navigation-drawer v-model="outputDialog" fixed temporary right stateless width="600" id="output-dialog">
        <v-card flat>
          <v-card-title>
            <div class="title">支払予定を日付指定して計上</div>
          </v-card-title>
          <v-card-text>
            <v-form v-model="outputValid">
              <v-row no-gutters>
                <v-col cols="12" class="pa-2">
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">請求期日</th>
                          <th class="text-left">支払先</th>
                          <th class="text-right">合計金額（税込）</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in selectedOutputs" :key="item.id">
                          <td>{{ item.fixedDate }}</td>
                          <td>{{ item.customer.name }}</td>
                          <td class="text-right">&yen; {{ separate( Math.floor(item.amount) ) }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-col>
                <v-col cols="6" class="pa-2 mt-5">
                  <v-menu
                    v-model="outputPayment"
                    :close-on-content-click="false"
                    :nudge-right="0"
                    transition="scale-transition" offset-y min-width="290px">
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        ref="outputPaymentDate"
                        label="支払日"
                        v-model="outputPaymentDate"
                        :rules="[rules.required]"
                        class="required"
                        v-on="on"
                        outlined clearable hide-details="auto">
                      </v-text-field>
                    </template>
                    <v-date-picker v-model="outputPaymentDate" @input="outputPayment = false"></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" @click.stop="checkOutputs" :loading="saving" :disabled="!outputValid" text>
              <v-icon left>fas fa-save</v-icon>登録
            </v-btn>
            <v-btn @click="outputDialog = false" text>
              <v-icon left>fas fa-times</v-icon>閉じる
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-navigation-drawer>

      <!-- 請求書編集ダイアログ -->
      <Claim
        :editedItem="paymentItem"
        :customers="customers"
        :corporates="corporates"
        :show="claimDialog"
        @close="claimDialog = false" />

      <!-- 入金登録ダイアログ -->
      <v-navigation-drawer v-model="inputRegistDialog" fixed temporary right stateless width="600" id="input-regist-dialog">
        <v-card flat>
          <v-card-title>
            <div class="title">未請求の入金登録</div>
          </v-card-title>
          <v-card-text>
            <v-form v-model="inputRegistValid">
              <v-row no-gutters>
                <v-col cols="12" class="pa-2">
                  <v-text-field
                    ref="inputRegistTitle"
                    label="件名"
                    v-model="inputRegistItem.title"
                    :rules="[rules.required]"
                    class="required"
                    outlined hide-details="auto">
                  </v-text-field>
                </v-col>
                <v-col cols="12" class="pa-2">
                  <v-combobox
                    ref="inputRegistCorporate"
                    label="発行元"
                    v-model="inputRegistItem.corporateID"
                    :items="corporateLabels"
                    :rules="[rules.required]"
                    class="required"
                    append-icon="fas fa-list-ol"
                    @click.stop="openSelected('inputRegistCorporate')"
                    readonly outlined hide-details="auto">
                  </v-combobox>
                </v-col>
                <v-col cols="12" class="pa-2">
                  <v-combobox
                    ref="inputRegistCustomer"
                    label="客先"
                    v-model="inputRegistItem.customerID"
                    :items="customers"
                    :rules="[rules.required]"
                    class="required"
                    append-icon="fas fa-list-ol"
                    @click.stop="openSelected('inputRegistCustomer', true)"
                    readonly outlined hide-details="auto">
                  </v-combobox>
                </v-col>
                <v-col cols="6" class="pa-2">
                  <v-text-field
                    ref="inputRegistAmount"
                    label="入金額"
                    type="number"
                    v-model="inputRegistItem.amount"
                    :rules="[rules.required]"
                    class="required"
                    outlined hide-details="auto">
                  </v-text-field>
                </v-col>
                <v-col cols="6" class="pa-2">
                  <v-menu
                    v-model="inputRegistPayment"
                    :close-on-content-click="false"
                    :nudge-right="0"
                    transition="scale-transition" offset-y min-width="290px">
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        ref="inputRegistPaymentDate"
                        label="入金日"
                        v-model="inputRegistItem.paymentDate"
                        :rules="[rules.required]"
                        class="required"
                        v-on="on"
                        outlined clearable hide-details="auto">
                      </v-text-field>
                    </template>
                    <v-date-picker v-model="inputRegistItem.paymentDate" @input="inputRegistPayment = false"></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" class="pa-2">
                  <v-combobox
                    ref="inputRegistPayment"
                    label="支払方法"
                    v-model="inputRegistItem.payment"
                    :items="payment"
                    :rules="[rules.required]"
                    class="required"
                    outlined hide-details="auto">
                  </v-combobox>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" @click.stop="inputRegistSave" :loading="saving" :disabled="!inputRegistValid" text>
              <v-icon left>fas fa-save</v-icon>登録
            </v-btn>
            <v-btn @click="inputRegistDialog = false" text>
              <v-icon left>fas fa-times</v-icon>閉じる
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-navigation-drawer>

      <Loading :visible="loading"></Loading>

    </div>
  </v-container>
</template>

<script>
import { API, graphqlOperation } from 'aws-amplify'
import { 
  listAppOptions,
  listUsers, 
  listOrders, 
  listPayments, 
  listProjects, 
  listProcesses, 
  listCustomers, 
  listFacilityReserves, 
  listFacilities,
  listEvents,
  listCorporates,
  getUser,
  getSequence } from '../graphql/queries'
import { 
  updateOrder, updatePayment, createCustomer, createOrder,
  createSequence, updateSequence,
  updateUser,
  createPaymentDoc } from '../graphql/mutations'
import { 
  onCreateOrder, onUpdateOrder, onDeleteOrder,
  onCreatePayment, onUpdatePayment, onDeletePayment,
  onUpdateUser } from '../graphql/subscriptions'
import moment from 'moment'
import WeeklySales from '@/components/WeeklySales'
import MonthlyReserve from '@/components/MonthlyReserve'
import ScheduleTimeline from '@/components/ScheduleTimeline'
import Claim from '@/components/Claim'
import MultiUpload from '@/components/MultiUpload'

export default {
  data() {
    return {
      loading: true,
      from: null,
      to: null,
      span: 13, //1週間=6, 2週間=13, 表示したい期間-1を指定
      weeklySalesOptions: {
        height: 455,
      },
      MonthlyReserveOptions: {
        height: null,
      },
      historyHeaders: [
        { text: '請求日', align: 'start', value: 'claimDate', width:120 },
        { text: '客先', align: 'start', value: 'customer' },
        { text: '件名', align: 'start', value: 'title' },
        { text: '入金', align: 'start', value: 'complete' },
        { text: '合計金額（税込）', align: 'end', value: 'amount' },
      ],
      inputHeaders: [
        { text: '請求日', align: 'start', value: 'claimDate', width:120 },
        { text: '請求期日', align: 'start', value: 'fixedDate', width:120 },
        { text: '客先', align: 'start', value: 'customer' },
        { text: '合計金額（税込）', align: 'end', value: 'amount' },
      ],
      outputHeaders: [
        { text: '請求日', align: 'start', value: 'claimDate', width:120 },
        { text: '支払期日', align: 'start', value: 'fixedDate', width:120 },
        { text: '支払先', align: 'start', value: 'customer' },
        { text: '支払金額（税込）', align: 'end', value: 'amount' },
      ],
      orders: [],
      payments: [],
      users: [],
      projects: [],
      tasks: [],
      yearStart: null,
      yearEnd: null,
      monthStart: null,
      monthEnd: null,
      customers: [],
      corporates: [],
      facilities: [],
      reserves: [],
      appOptions: {},
      //入金予定
      selectedInputs: [],
      inputDialog: false,
      inputValid: false,
      inputPayment: false,
      inputPaymentDate: null,
      //支払予定
      selectedOutputs: [],
      paymentItem: {},
      claimDialog: false,
      outputDialog: false,
      outputValid: false,
      outputPayment: false,
      outputPaymentDate: null,
      saving: false,
      //入金登録
      inputRegistDialog: false,
      inputRegistValid: false,
      inputRegistItem: {},
      inputRegistPayment: false,
      inputRegistRefs: [
        'inputRegistTitle',
        'inputRegistCustomer',
        'inputRegistAmount',
        'inputRegistPaymentDate',
        'inputRegistPayment',
      ],
      //カレンダー
      events: [],
      //ウィジェット
      widgets: [
        { name: 'カレンダー', allowed: null },
        { name: '入金予定', allowed: '見積・請求' },
        { name: '支払予定', allowed: '発注・支払' },
        { name: '請求書類', allowed: '発注・支払' },
        { name: '売上グラフ', allowed: '見積・請求' },
        { name: '売上履歴', allowed: '見積・請求' },
        { name: '月間予約時間', allowed: '施設予約' },
        { name: '工程表', allowed: 'プロジェクト' },
      ],
      widgetSetting: false,
      //フィルタ
      filterKeyword: {},
      //まとめてアップロード
      paymentDocs: [],
      // 有給
      paidRemain: null,
      paidHoursUsed: null,
      dailyWorktime: 0,
    }
  },
  components: {
    WeeklySales,
    MonthlyReserve,
    ScheduleTimeline,
    Claim,
    MultiUpload,
  },
  prop: {},
  mounted: async function() {
    let timer = setInterval(() => {
      if (this.getMyself()) {
        clearInterval(timer)
        this.init();
      }
    }, 100)
    
    //order 作成時 subscription
    await API.graphql(graphqlOperation(onCreateOrder)).subscribe({
      next: (e) => {
        const row = e.value.data.onCreateOrder
        this.orders.push(row)
      }
    })
    //order 更新時 subscription
    await API.graphql(graphqlOperation(onUpdateOrder)).subscribe({
      next: (e) => {
        const row = e.value.data.onUpdateOrder
        this.orders.find(origin => {
          if (origin.id === row.id) {
            origin = Object.assign(origin, row)
          }
        })
      }
    })
    //order 削除時 subscription
    await API.graphql(graphqlOperation(onDeleteOrder)).subscribe({
      next: (e) => {
        const row = e.value.data.onDeleteOrder
        this.orders = this.orders.filter(origin => origin.id !== row.id)
      }
    })
    //payment 作成時 subscription
    await API.graphql(graphqlOperation(onCreatePayment)).subscribe({
      next: (e) => {
        const row = e.value.data.onCreatePayment
        this.payments.push(row)
      }
    })
    //payment 更新時 subscription
    await API.graphql(graphqlOperation(onUpdatePayment)).subscribe({
      next: (e) => {
        const row = e.value.data.onUpdatePayment
        this.payments.find(origin => {
          if (origin.id === row.id) {
            origin = Object.assign(origin, row)
          }
        })
      }
    })
    //payment 削除時 subscription
    await API.graphql(graphqlOperation(onDeletePayment)).subscribe({
      next: (e) => {
        const row = e.value.data.onDeletePayment
        this.payments = this.payments.filter(origin => origin.id !== row.id)
      }
    })
    //更新時 subscription
    await API.graphql(graphqlOperation(onUpdateUser)).subscribe({
      next: (e) => {
        const user = e.value.data.onUpdateUser
        this.$store.dispatch('updateMyself', Object.assign({
          name: user.last + ' ' + user.first,
        }, user))
      }
    })
  },
  created() {},
  computed: {
    nowDate() {
      return moment().format('M月D日') + '<span>' + moment().format('dddd') + '</span>'
    },
    nowMonth() {
      return moment(this.monthStart).format('YYYY年M月')
    },
    nowYear() {
      return moment(this.yearStart).format('YYYY年度')
    },
    monthlySalesData() {
      const proceed = Object.assign({}, {})
      const cost = Object.assign({}, {})
      const labels = []
      // 売上
      const dataProceed = []
      // 経費
      const dataCost = []
      // 期間の算出
      let start = moment(this.yearStart, 'YYYY-MM-DD').format('YYYY-MM-DD')
      let end =  moment(this.yearEnd, 'YYYY-MM-DD').format('YYYY-MM-DD')
      if (this.filterKeyword.corporate) {
        const corporate = this.corporates.find(e => e.id === this.filterKeyword.corporate.value)
        const startMonth = corporate.startMonth ? corporate.startMonth - 1 : 0
        start = moment(this.yearStart, 'YYYY-MM-DD').month(startMonth).startOf('months').format('YYYY-MM-DD')
        end = moment(start, 'YYYY-MM-DD').add(1, 'years').subtract(1, 'days').format('YYYY-MM-DD')
      }
      // 合計を0で初期化
      for (let i=0; i<12; i+=1) {
        const dat = moment(start, 'YYYY-MM-DD').add(i, 'months').format('Y/M')
        proceed[dat] = 0
        cost[dat] = 0
      }
      // 売上に加算
      for (const item of this.orders) {
        if (!item.claimDate || 
            moment(start, 'YYYY-MM-DD') > moment(item.claimDate, 'YYYY-MM-DD') || 
            moment(end, 'YYYY-MM-DD').add(1, 'days') <= moment(item.claimDate, 'YYYY-MM-DD')) continue
        //担当企業が選択されていれば担当企業のみ集計対象
        if (this.filterKeyword.corporate) {
          const targetID = this.filterKeyword.corporate.value
          if (item.corporateID != targetID) continue
        }

        const dat = moment(item.claimDate, 'YYYY-MM-DD').format('Y/M')
        const value = Math.floor(item.amount * (item.tax / 100 + 1))
        proceed[dat] += value
      }
      // 経費に加算
      for (const item of this.payments) {
        if (!item.claimDate || 
            moment(start, 'YYYY-MM-DD') > moment(item.claimDate, 'YYYY-MM-DD') || 
            moment(end, 'YYYY-MM-DD').add(1, 'days') <= moment(item.claimDate, 'YYYY-MM-DD')) continue
        //担当企業が選択されていれば担当企業のみ集計対象
        if (this.filterKeyword.corporate) {
          const targetID = this.filterKeyword.corporate.value
          if (item.corporateID != targetID) continue
        }

        const dat = moment(item.claimDate, 'YYYY-MM-DD').format('Y/M')
        const value = item.amount
        cost[dat] += value
      }
      //グラフデータ用に成型
      Object.keys(proceed).forEach(function (key) {
        labels.push(key)
        dataProceed.push(proceed[key])
        dataCost.push(cost[key])
      })
      //グラフデータの作成
      return {
        labels: labels,
        datasets: [
          {
            data: dataProceed,
            fill: false,
            pointBackgroundColor: 'rgba(255, 99, 132, 1)',
            borderColor: 'rgba(255, 99, 132, 1)',
            borderWidth: 2,
            lineTension: 0,
            label: '売上',
          },
          {
            data: dataCost,
            fill: false,
            pointBackgroundColor: 'rgba(132, 99, 255, 1)',
            borderColor: 'rgba(132, 99, 255, 1)',
            borderWidth: 2,
            lineTension: 0,
            label: '経費',
          },
        ]
      }
    },
    orderHistory() {
      const map = []
      for (const item of this.orders) {
        if (!item.claimDate || 
            moment(this.monthStart) > moment(item.claimDate) || 
            moment(this.monthEnd).add(1, 'days') <= moment(item.claimDate)) continue
        //担当企業が選択されていれば担当企業のみ集計対象
        if (this.filterKeyword.corporate) {
          const targetID = this.filterKeyword.corporate.value
          if (item.corporateID != targetID) continue
        }

        map.push(item)
      }
      return map
    },
    historyTotalValue() {
      let sum = 0
      for (const item of this.orderHistory) {
        sum += Math.floor( Math.floor(item.amount * (item.tax / 100 + 1)) )
      }
      return sum
    },
    historyTotalResult() {
      let sum = 0
      for (const item of this.orderHistory) {
        if (!item.complete) continue
        sum += Math.floor( Math.floor(item.amount * (item.tax / 100 + 1)) )
      }
      return sum
    },
    resources() {
      const map = []
      for (const item of this.tasks) {
        //プロジェクト
        const project = this.projects.find(e => e.id === item.id)
        if (project || item.project) {
          if (this.filterKeyword.corporate) {
            const targetID = this.filterKeyword.corporate.value
            if (project && project.corporateID != targetID) continue
            if (item.project && item.project.corporateID != targetID) continue
          }
        }
        //担当
        const charge = []
        if (item.charge) {
          for (const id of item.charge) {
            const user = this.users.find(e => e.value === id)
            charge.push(user.text || id)
          }
        }

        map.push(Object.assign({
          chargeText: charge.join(', ')
        }, item))
      }
      return map
    },
    //月間施設予約データ
    monthlyReserveData() {
      const map = []
      const datasets = []
      const labels = []
      const colors = [
        { backgroundColor: 'rgba(255, 99, 132, 0.2)', borderColor: 'rgba(255, 99, 132, 1)' },
        { backgroundColor: 'rgba(132, 99, 255, 0.2)', borderColor: 'rgba(132, 99, 255, 1)' },
      ]

      for (const reserve of this.reserves) {
        const start = moment(reserve.start)
        const end = moment(reserve.end)
        const hour = end.diff(start, 'minutes')
        const customer = map.find(e => e.customerID == reserve.customerID)
        //対象があるか
        if (customer) {
          customer.values[reserve.facilityID] += hour
        } else {
          const data = {
            customerID: reserve.customerID,
            values: {},
          }
          for (const facility of this.facilities) {
            data.values[facility.id] = 0
          }
          data.values[reserve.facilityID] += hour
          
          map.push(data)
        }
      }

      this.facilities.forEach((facility, index) => {
        datasets.push({
          label: facility.group + ' ' + facility.name,
          backgroundColor: colors[index] ? colors[index].backgroundColor : 'rgba(132, 99, 255, 0.2)',
          borderColor: colors[index] ? colors[index].borderColor : 'rgba(132, 99, 255, 1)',
          borderWidth: 1,
          data: [],
        })
      })

      for (const item of map) {
        const customer = this.customers.find(e => e.id == item.customerID)
        labels.push(customer.name || '')
        let index = 0
        Object.keys(item.values).forEach(id => {
          datasets[index].data.push( (item.values[id] / 60).toFixed(2) )
          index++
        })
      }

      return {
        datasets: datasets,
        labels: labels,
      }
    },
    //入金予定アイテム
    filterInputPayments() {
      const map = []
      for (const item of this.orders) {
        if (!item.complete && item.claimDate) {
          if (this.filterKeyword.corporate) {
            const targetID = this.filterKeyword.corporate.value
            if (item.corporateID != targetID) continue
          }
          map.push(item)
        }
      }

      return map
    },
    //支払予定アイテム
    filterOutputPayments() {
      const map = []
      for (const item of this.payments) {
        if (!item.complete) {
          if (this.filterKeyword.corporate) {
            const targetID = this.filterKeyword.corporate.value
            if (item.corporateID != targetID) continue
          }
          map.push(item)
        }
      }

      return map
    },
    corporateLabels() {
      const map = []

      for (const item of this.corporates) {
        map.push({
          value: item.id,
          text: item.name,
        })
      }

      return map
    },
    userWidgets() {
      const map = []

      for (const item of this.widgets) {
        if (!item.allowed || this.isAllowed(item.allowed)) {
          map.push(item)
        }
      }

      return map
    },
  },
  watch: {
    inputDialog(val) {
      if (!val) {
        this.$refs['inputPaymentDate'].reset()
        this.paymentDate = null
      }
    },
    outputDialog(val) {
      if (!val) {
        this.$refs['outputPaymentDate'].reset()
        this.paymentDate = null
      }
    },
    inputRegistDialog(val) {
      if (!val) {
        for (const item of this.inputRegistRefs) {
          this.$refs[item].reset()
        }
      }
    },
  },
  methods: {
    async init() {
      this.from = moment().add(-this.span, 'day').format('YYYY-MM-DDT00:00:00')
      this.to = moment().format('YYYY-MM-DDT23:59:59')
      this.monthStart = moment().startOf('month').format('YYYY-MM-DD')
      this.monthEnd = moment(this.monthStart).endOf('month').format('YYYY-MM-DD')
      //this.yearStart = moment().startOf('year').format('YYYY-MM-DD')
      //this.yearEnd = moment(this.yearStart).endOf('year').format('YYYY-MM-DD')
      //設定 query
      const appOptionQuery = await API.graphql(graphqlOperation(listAppOptions, { limit: 100 }))
      for (const row of appOptionQuery.data.listAppOptions.items) {
        this.appOptions[row.name] = row.value
      }

      //ウィジェットの初期化
      if (!this.getMyself().widgets) {
        const map = []
        for (const item of this.widgets) {
          map.push({
            name: item.name,
            enabled: true,
          })
        }
        //ユーザー情報の更新
        await API.graphql(graphqlOperation(updateUser, {input: {
          id: this.getMyself().id,
          widgets: map,
        }}))
      }

      if (this.getMyself()) {
        //ユーザー情報の取得
        const user = await API.graphql(graphqlOperation(getUser, {id: this.getMyself().id}))
        this.paidRemain = user.data.getUser.paidRemain
        this.paidHoursUsed = user.data.getUser.paidHoursUsed
        this.dailyWorktime = user.data.getUser.dailyWorktime || 0
      }

      //ユーザー query
      await this.getUsers()
      //受注一覧 query
      await this.getOrders()
      //請求書一覧 query
      await this.getPayments()
      //プロジェクト query
      await this.getProjects()
      //工程 query
      await this.getProcesses()
      //客先 query
      await this.getCustomers()
      //施設 query
      await this.getFacilities()
      //施設予約 query
      this.getMonthReserve()
      //企業情報 query
      await this.getCorporates()

      //デフォルト企業の選択
      this.$set(this.filterKeyword, 'corporate', this.corporateLabels[0])
      const corporate = this.corporates.find(e => e.id === this.filterKeyword.corporate.value)
      const month = corporate.startMonth ? corporate.startMonth - 1 : 0
      this.yearStart = moment().month(month).startOf('month').format('YYYY-MM-DD')
      if (moment(this.yearStart, 'YYYY-MM-DD') > moment()) {
        this.yearStart = moment(this.yearStart, 'YYYY-MM-DD').subtract(1, 'years').format('YYYY-MM-DD')
      }
      this.yearEnd = moment(this.yearStart, 'YYYY-MM-DD').add(1, 'years').subtract(1, 'days').format('YYYY-MM-DD')

      this.loading = false
    },
    //ウィジェットの表示可否
    isShow(name) {
      if (this.getMyself()) {
        const target = this.widgets.find(e => e.name == name)
        if (!target.allowed || this.isAllowed(target.allowed)) {
          const selfWidgets = this.getMyself().widgets || []
          const widget = selfWidgets.find(e => e.name == name)
          if (widget && widget.enabled) return true
        }
      }

      return false
    },
    //ウィジェットの表示切替
    async showChange(name) {
      const widgets_org = this.getMyself().widgets
      const widgets = []
      for (const widget of widgets_org) {
        widgets.push({
          name: widget.name,
          enabled: widget.enabled,
        })
      }
      //設定が存在する場合
      const item = widgets.find(e => e.name == name)
      if (item) {
        item.enabled = !item.enabled

      //設定が存在しない場合
      } else {
        widgets.push({
          name: name,
          enabled: true,
        })
      }
      
      //ユーザー情報の更新
      await API.graphql(graphqlOperation(updateUser, {input: {
        id: this.getMyself().id,
        widgets: widgets,
      }}))

      this.redraw()
    },
    //写真のプレビューを表示
    photoPreview(src) {
      //画像はポップアップで表示
      this.$store.dispatch('updatePreview', {
        visible: true,
        src: src,
      })
    },
    //請求書まとめてアップロード
    async paymentDocMultiUpload() {
      //企業を取得
      let corporateID = this.corporateLabels[0].value
      if (this.filterKeyword.corporate) {
        corporateID = this.filterKeyword.corporate.value
      }
      //請求書を登録
      for (const item of this.paymentDocs) {
        const doc = {
          corporateID: corporateID,
          pic: item.key
        }
        await API.graphql(graphqlOperation(createPaymentDoc, {input: doc}))
      }
      this.redraw()
      this.myAlert('アップロードが完了しました', 'success')
    },
    //表示日付
    getDisplayDate(dat) {
      return moment(dat).format('M/D')
    },
    //予約時間の取得
    async getMonthReserve() {
      this.reserves = []
      await this.getFacilityReserve()

      this.redraw()
    },
    //月間戻り
    monthPrev() {
      this.monthStart = moment(this.monthStart).subtract(1, 'months').format('YYYY-MM-DD')
      this.monthEnd = moment(this.monthStart).endOf('month').format('YYYY-MM-DD')

      this.getMonthReserve()
    },
    //月間進み
    monthNext() {
      this.monthStart = moment(this.monthStart).add(1, 'months').format('YYYY-MM-DD')
      this.monthEnd = moment(this.monthStart).endOf('month').format('YYYY-MM-DD')

      this.getMonthReserve()
    },
    //年戻り
    yearPrev() {
      this.yearStart = moment(this.yearStart).subtract(1, 'years').format('YYYY-MM-DD')
      this.yearEnd = moment(this.yearStart).endOf('years').format('YYYY-MM-DD')

      this.getMonthReserve()
    },
    //年進み
    yearNext() {
      this.yearStart = moment(this.yearStart).add(1, 'years').format('YYYY-MM-DD')
      this.yearEnd = moment(this.yearStart).endOf('years').format('YYYY-MM-DD')

      this.getMonthReserve()
    },
    //企業選択
    redraw() {
      setTimeout(() => {
        this.$redrawVueMasonry('dashboard')
      }, 0)
    },
    //行選択
    async onClaimClick(params) {
      try {
        this.paymentItem = Object.assign({
          items: [],
        }, params)

        //請求先
        this.paymentItem.corporateID = this.corporateLabels.find(e => e.value == params.corporateID)
        //客先
        this.paymentItem.customerID = this.customers.find(e => e.id == params.customerID)
        this.claimDialog = true

      } catch (e) {
        //console.log(e)
      }
    },
    //入金の一括チェック
    async checkInputs() {
      if (confirm('選択した伝票を一括確認しますか？')) {
        const date = this.inputPaymentDate
        for (const item of this.selectedInputs) {
          const data = {
            id: item.id,
            paymentDate: date,
            complete: true,
          }
          await API.graphql(graphqlOperation(updateOrder, {input: data}))
          this.log({ type: this.$route.name, text: '[' + data.id + '] を更新しました' })
        }

        this.selectedInputs = []
        this.inputDialog = false
      }
    },
    //支払の一括チェック
    async checkOutputs() {
      if (confirm('選択した請求書を一括確認しますか？')) {
        const date = this.outputPaymentDate
        for (const item of this.selectedOutputs) {
          const data = {
            id: item.id,
            paymentDate: date,
            complete: true,
          }
          await API.graphql(graphqlOperation(updatePayment, {input: data}))
          this.log({ type: this.$route.name, text: '[' + data.title + '] を更新しました' })
        }

        this.selectedOutputs = []
        this.outputDialog = false
      }
    },
    //未請求の入金登録
    async inputRegistSave() {
      //console.log(this.inputRegistItem)
      try {
        this.saving = true
        const data = {
          customerID: this.inputRegistItem.customerID ? this.inputRegistItem.customerID.value: null,
          corporateID: this.inputRegistItem.corporateID ? this.inputRegistItem.corporateID.value: null,
          title: this.inputRegistItem.title || null,
          amount: this.inputRegistItem.amount,
          tax: 0,
          claimDate: this.inputRegistItem.paymentDate,
          paymentDate: this.inputRegistItem.paymentDate,
          payment: this.inputRegistItem.payment,
          complete: true,
        }

        //客先が追加された場合
        if (!data.customerID) {
          const customer = {
            name: this.inputRegistItem.customerID,
            payment: ['銀行振込'],
          }
          const ret = await API.graphql(graphqlOperation(createCustomer, {input: customer}))
          this.log({ type: this.$route.name, text: '請求書にて [' + customer.name + '] を追加しました' })
          //追加した客先IDを設定
          data.customerID = ret.data.createCustomer.id
        }
        //追加
        data.id = await this.createOrderID()
        await API.graphql(graphqlOperation(createOrder, {input: data}))
        this.log({ type: this.$route.name, text: '[' + data.title + '] を追加しました' })
        this.myAlert(data.title + 'を追加しました', 'success')
        
        this.inputRegistDialog = false

      } catch (error) {
        this.myAlert(error.message)
        this.saving = false
      }
    },
    //伝票Noの生成
    async createOrderID() {
      //let count = 1
      const name = 'ORDER_' + (this.appOptions.orderDateFormat ? moment().format(this.appOptions.orderDateFormat) : '')
      const tag = 'Order'

      //連番キーがあるか
      const find = await API.graphql(graphqlOperation(getSequence, {
        name: name,
        tag: tag,
      }))

      const data = {
        name: name,
        tag: tag,
        count: find.data.getSequence ? find.data.getSequence.count + 1 : 1,
      }

      if (find.data.getSequence) {
        await API.graphql(graphqlOperation(updateSequence, { input: data }))
      } else {
        await API.graphql(graphqlOperation(createSequence, { input: data }))
      }

      //伝票Noの生成
      const prefix = this.appOptions.orderPrefix || ''
      const dateFormat = this.appOptions.orderDateFormat ? moment().format(this.appOptions.orderDateFormat) : ''
      const noDigit = this.appOptions.orderNoDigit || null
      const digit = noDigit ? this.zeroPadding(data.count, parseInt(noDigit)) : data.count
      const orderID = prefix + dateFormat + digit

      return orderID
    },
    //ユーザーの取得
    async getUsers(nextToken = null) {
      const query = await API.graphql(graphqlOperation(listUsers, { limit: 100, nextToken: nextToken }))
      for (const data of query.data.listUsers.items) {
        this.users.push({
          value: data.id,
          text: data.last + ' ' + data.first
        })
      }
      if (query.data.listUsers.nextToken) this.getUsers(query.data.listUsers.nextToken)
    },
    //受注リストの取得
    async getOrders(nextToken = null) {
      //所属企業
      const myself = this.getMyself()
      const corporateFilter = []
      for (const id of myself.corporateIDs) {
        corporateFilter.push({
          corporateID: { eq: id }
        })
      }
      const query = await API.graphql(graphqlOperation(listOrders, {
        filter: {
          or: corporateFilter
        },
        limit: 100,
        nextToken: nextToken
      }))
      for (const row of query.data.listOrders.items) {
        this.orders.push(row)
      }
      if (query.data.listOrders.nextToken) {
        this.getOrders(query.data.listOrders.nextToken)
      }
    },
    //請求書リストの取得
    async getPayments(nextToken = null) {
      //所属企業
      const myself = this.getMyself()
      const corporateFilter = []
      for (const id of myself.corporateIDs) {
        corporateFilter.push({
          corporateID: { eq: id }
        })
      }
      const query = await API.graphql(graphqlOperation(listPayments, {
        filter: {
          or: corporateFilter
        },
        limit: 100,
        nextToken: nextToken
      }))
      for (const row of query.data.listPayments.items) {
        this.payments.push(row)
      }
      if (query.data.listPayments.nextToken) {
        this.getPayments(query.data.listPayments.nextToken)
      }
    },
    //プロジェクトの取得
    async getProjects(nextToken = null) {
      //所属企業
      const myself = this.getMyself()
      const corporateFilter = []
      for (const id of myself.corporateIDs) {
        corporateFilter.push({
          corporateID: { eq: id }
        })
      }
      const query = await API.graphql(graphqlOperation(listProjects, { 
        filter: {
          or: corporateFilter
        },
        limit: 100, 
        nextToken: nextToken 
      }))
      for (const data of query.data.listProjects.items) {
        this.projects.push(data)
      }
      if (query.data.listProjects.nextToken) {
        this.getProjects(query.data.listProjects.nextToken)
      }
    },
    //工程の取得
    async getProcesses(nextToken = null) {
      //所属企業
      const myself = this.getMyself()
      const corporateFilter = []
      for (const id of myself.corporateIDs) {
        corporateFilter.push({
          corporateID: { eq: id }
        })
      }
      //query
      const query = await API.graphql(graphqlOperation(listProcesses, {
        filter: {
          or: [
            { start: { ge: moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD') } },
            { end: { ge: moment().startOf('months').format('YYYY-MM-DD') } },
          ],
        },
        limit: 100,
        nextToken: nextToken
      }))
      for (const data of query.data.listProcesses.items) {
        let parent = this.tasks.find(e => e.id == data.projectID)
        //親としてプロジェクトを生成
        if (!parent) {
          const project = this.projects.find(e => e.id == data.projectID)
          if (!project) continue

          parent = {
            id: data.projectID,
            name: project.customer.name + ' [' + project.name + ']',
            start: data.start,
            end: data.end,
            color: '#666',
            progress: 0,
            sub: 0,
            days: 0,
          }
          this.tasks.push(parent)
        }
        const start = moment(data.start)
        const end = moment(data.end)
        if (moment(parent.start) > start) parent.start = data.start
        if (moment(parent.end) < end) parent.end = data.end
        //進捗の計算
        const days = end.diff(start, 'days') + 1
        parent.sub += data.progress ? days * (data.progress / 100) : 0
        parent.days += days
        parent.progress = parent.sub / parent.days * 100
        
        if (!data.parentID) {
          data.parentID = data.projectID
        }
        this.tasks.push(data)
      }
      //開始時間でソート
      this.tasks.sort(function(a,b) {
        if (a.start < b.start) return -1
        if (a.start > b.start) return 1
        return 0
      })
      if (query.data.listProcesses.nextToken) {
        this.getProcesses(query.data.listProcesses.nextToken)
      }
    },
    //客先の取得
    async getCustomers(nextToken = null) {
      const query = await API.graphql(graphqlOperation(listCustomers, { limit: 100, nextToken: nextToken }))
      for (const data of query.data.listCustomers.items) {
        this.customers.push(Object.assign({
          value: data.id,
          text: data.name,
        }, data))
      }
      if (query.data.listCustomers.nextToken) this.getCustomers(query.data.listCustomers.nextToken)
    },
    //企業の取得
    async getCorporates(nextToken = null) {
      //所属企業
      const myself = this.getMyself()
      const corporateFilter = []
      for (const id of myself.corporateIDs) {
        corporateFilter.push({
          id: { eq: id }
        })
      }
      //query
      const query = await API.graphql(graphqlOperation(listCorporates, { 
        filter: {
          or: corporateFilter
        },
        limit: 100, 
        nextToken: nextToken 
      }))
      for (const data of query.data.listCorporates.items) {
        this.corporates.push(data)
      }
      if (query.data.listCorporates.nextToken) this.getCorporates(query.data.listCorporates.nextToken)
    },
    //施設の取得
    async getFacilities(nextToken = null) {
      const query = await API.graphql(graphqlOperation(listFacilities, { limit: 100, nextToken: nextToken }))
      for (const data of query.data.listFacilities.items) {
        this.facilities.push(data)
      }
      if (query.data.listFacilities.nextToken) this.getFacilities(query.data.listFacilities.nextToken)
    },
    //予約情報の取得
    async getFacilityReserve(nextToken = null) {
      const query = await API.graphql(graphqlOperation(listFacilityReserves, {
        filter: {
          and: [
            { start: { ge: this.monthStart } },
            { end: { le: this.monthEnd } },
          ],
        },
        limit: 100,
        nextToken: nextToken
      }))
      for (const row of query.data.listFacilityReserves.items) {
        this.reserves.push(row)
      }
      if (query.data.listFacilityReserves.nextToken) this.getFacilityReserve(query.data.listFacilityReserves.nextToken)
    },
    //ビュー変更
    async viewChange({start, end}) {
      this.events = []
      await this.getEvents(
        moment(start.date).subtract(7, 'days').format('YYYY-MM-DD'), 
        moment(end.date).add(7, 'days').format('YYYY-MM-DD')
      )
    },
    //イベントの取得
    async getEvents(start, end, nextToken = null) {
      const query = await API.graphql(graphqlOperation(listEvents, {
        filter: {
          and: [
            { start: { ge: moment(start).subtract(1, 'day').format('YYYY-MM-DDTHH:mm:ss') } },
            { end: { le: moment(end).format('YYYY-MM-DDTHH:mm:ss') } },
          ],
        },
        limit: 100,
        nextToken: nextToken
      }))
      for (const data of query.data.listEvents.items) {
        this.events.push(data)
      }
      if (query.data.listEvents.nextToken) {
        this.getEvents(start, end, query.data.listEvents.nextToken)
      }
    },
  },
}
</script>

<style lang='scss'>
.card-grid {
  .row > div {
    padding: 0 4px;
  }
  .grid-item {
    width: 50%;
    padding: 0 4px;
    &.full {
      width: 100%;
    }
    .item {
      padding: 8px;
      border: 1px solid #CCC;
      border-radius: 5px;
      vertical-align: top;
      margin-bottom: 7px;
      background: #fff;
      .single {
        width: 200px;
        height: 150px;
        .today {
          font-size: 24px;
          text-align: center;
          span {
            display: block;
            font-size: 14px;
          }
        }
      }
      .title {
        border-left: 7px solid #578aa9;
        padding-left: 10px;
        margin-bottom: 10px;
        font-size: 16px !important;
        position: relative;
        .ctrl-btns {
          position: absolute;
          top: 0;
          right: 0;
        }
      }
      .total {
        text-align: right;
        font-size: 16px;
        .label {
          padding: 10px;
        }
        .value {
          display: inline-block;
          background: #EEE;
          padding: 10px;
          width: 200px;
        }
      }
    }
  }
}
.widget-ctrl {
  .v-btn {
    height: 40px !important;
    border: 1px solid #999;
  }
}
.widgets {
  li {
    border: 1px dashed #CCC;
    width: 150px;
    margin: 10px;
    line-height: 100px;
    border-radius: 5px;
    color: #CCC;
    cursor: pointer;
    &.active {
      border: 2px solid #0277bd;
      color: #333;
    }
  }
}
@media screen and (max-width: 768px) {
  .card-grid {
    .grid-item {
      width: 100%;
    }
  }
}
</style>
